import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Upload, Download, Send, Trash2, RefreshCw, CheckCircle, XCircle, Clock, Zap, ArrowLeft, FileText } from '../components/Icons';
import { toast } from 'react-hot-toast';
import { apiGet, apiPost, apiDelete } from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL || '';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const statusBadge = (status) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'partial':
      return 'bg-yellow-100 text-yellow-800';
    case 'processing':
      return 'bg-blue-100 text-blue-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const DirectFbrSubmission = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const res = await apiGet('/api/bulk/batches?limit=25');
      if (res.ok) {
        setBatches(res.data.batches || []);
      } else {
        toast.error('Failed to load submission batches');
      }
    } catch (e) {
      toast.error('Failed to load submission batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/bulk/template`, { headers: authHeaders() });
      if (!res.ok) throw new Error('template');
      const blob = await res.blob();
      triggerDownload(blob, 'direct_fbr_template.csv');
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Upload a CSV then immediately submit it to FBR
  const handleUploadAndSubmit = async () => {
    if (!csvFile) {
      toast.error('Please choose a CSV file first');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const uploadRes = await fetch(`${API_BASE}/api/bulk/upload-csv`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok) {
        throw new Error(uploadData.detail || 'Upload failed');
      }
      const batchId = uploadData.batch_id;
      toast.success(`Uploaded ${uploadData.total_invoices} invoice(s). Submitting to FBR…`);

      const submitRes = await apiPost(`/api/bulk/submit/${batchId}`, {});
      if (submitRes.ok) {
        const { successful, failed } = submitRes.data;
        toast.success(`FBR submission done: ${successful} succeeded, ${failed} failed`);
      } else {
        toast.error(submitRes.data?.detail || 'FBR submission failed');
      }
      setCsvFile(null);
      const input = document.getElementById('direct-csv-input');
      if (input) input.value = '';
      fetchBatches();
    } catch (e) {
      toast.error(e.message || 'Upload/submit failed');
    } finally {
      setUploading(false);
    }
  };

  const resubmitFailed = async (batchId) => {
    setSubmittingId(batchId);
    try {
      const res = await apiPost(`/api/bulk/retry-failed/${batchId}`, {});
      if (res.ok) {
        toast.success(`Retried: ${res.data.successful} succeeded, ${res.data.failed} failed`);
        fetchBatches();
      } else {
        toast.error(res.data?.detail || 'Retry failed');
      }
    } catch (e) {
      toast.error('Retry failed');
    } finally {
      setSubmittingId(null);
    }
  };

  const downloadResults = async (batchId) => {
    try {
      const res = await fetch(`${API_BASE}/api/bulk/download/${batchId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('download');
      const blob = await res.blob();
      triggerDownload(blob, `direct_fbr_results_${batchId.slice(0, 8)}.csv`);
      toast.success('Results downloaded');
    } catch (e) {
      toast.error('Failed to download results');
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Deactivate this submission batch?')) return;
    try {
      const res = await apiDelete(`/api/bulk/delete/${batchId}`);
      if (res.ok) {
        toast.success('Batch deactivated');
        fetchBatches();
      } else {
        toast.error(res.data?.detail || 'Failed to deactivate');
      }
    } catch (e) {
      toast.error('Failed to deactivate');
    }
  };

  return (
    <Navigation>
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-amber-100 text-amber-700">
                    <Zap className="h-5 w-5" />
                  </span>
                  <h1 className="text-3xl font-bold text-slate-900">Direct FBR Submission</h1>
                </div>
                <p className="text-slate-600 mt-1">
                  Upload a CSV and submit invoices straight to FBR. Nothing is saved to your
                  clients, products, or invoices — you just get the FBR invoice IDs back.
                </p>
              </div>
              <button
                onClick={() => navigate('/batch-management')}
                className="text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg px-4 py-2 flex items-center hover:bg-slate-50 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Batch Management
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Upload card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 mb-8">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Upload &amp; Submit</h3>
                <button
                  onClick={downloadTemplate}
                  className="text-amber-700 hover:text-amber-800 text-sm font-medium flex items-center"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download CSV template
                </button>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <input
                  id="direct-csv-input"
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                />
                <button
                  onClick={handleUploadAndSubmit}
                  disabled={uploading || !csvFile}
                  className="bg-slate-700 text-white px-5 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-sm hover:shadow-md transition-all whitespace-nowrap"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit to FBR
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Rows sharing the same <span className="font-mono">invoiceRefNo</span> are grouped into one FBR invoice with multiple line items.
              </p>
            </div>
          </div>

          {/* Batches */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Submission Batches</h3>
              <button
                onClick={fetchBatches}
                className="text-slate-500 hover:text-slate-700 flex items-center text-sm"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                <p className="mt-3 text-slate-500">Loading…</p>
              </div>
            ) : batches.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No direct submissions yet. Upload a CSV above to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Batch</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Invoices</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Success / Failed</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {batches.map((b) => (
                      <tr key={b.batch_id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap font-mono text-xs text-slate-700">{b.batch_id.slice(0, 8)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge(b.status)}`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{b.total_items}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="text-green-600 font-medium">{b.successful_items}</span>
                          <span className="text-slate-400"> / </span>
                          <span className="text-red-600 font-medium">{b.failed_items}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {b.created_at ? new Date(b.created_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => downloadResults(b.batch_id)}
                              className="text-amber-600 hover:text-amber-800"
                              title="Download results (with FBR IDs)"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            {b.failed_items > 0 && (
                              <button
                                onClick={() => resubmitFailed(b.batch_id)}
                                disabled={submittingId === b.batch_id}
                                className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                title="Retry failed invoices"
                              >
                                <RefreshCw className={`h-4 w-4 ${submittingId === b.batch_id ? 'animate-spin' : ''}`} />
                              </button>
                            )}
                            <button
                              onClick={() => deleteBatch(b.batch_id)}
                              className="text-red-600 hover:text-red-800"
                              title="Deactivate batch"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Navigation>
  );
};

export default DirectFbrSubmission;
